import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { StickyNote, Save, Loader2 } from "lucide-react";
import { apiService } from "../../services/api.service";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";

interface RunNotesPanelProps {
  testId: string;
}

export const RunNotesPanel = ({ testId }: RunNotesPanelProps) => {
  const [notes, setNotes] = useState("");
  const [savedNotes, setSavedNotes] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadNotes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getLoadTestNotes(testId);
      setNotes(response.notes);
      setSavedNotes(response.notes);
      setUpdatedAt(response.updated_at);
    } catch (e) {
      console.error("Failed to load notes:", e);
      toast.error("Failed to load notes");
    } finally {
      setIsLoading(false);
    }
  }, [testId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await apiService.saveLoadTestNotes(testId, notes);
      setSavedNotes(response.notes);
      setUpdatedAt(response.updated_at);
      toast.success("Notes saved");
    } catch (e) {
      console.error("Failed to save notes:", e);
      toast.error("Failed to save notes");
    } finally {
      setIsSaving(false);
    }
  };

  const isDirty = notes !== savedNotes;

  return (
    <Card className="mt-4 border border-border/60 bg-white/80 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b border-border/60 pb-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted">
            <StickyNote className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex flex-col">
            <CardTitle className="text-sm font-semibold">Notes</CardTitle>
            {updatedAt && (
              <span className="text-[0.7rem] text-muted-foreground">
                Last saved {new Date(updatedAt).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={isSaving || isLoading || !isDirty}
          className="gap-1.5"
        >
          {isSaving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="h-3.5 w-3.5" />
          )}
          {isDirty ? "Save" : "Saved"}
        </Button>
      </CardHeader>
      <CardContent className="pt-3">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isLoading}
          rows={5}
          maxLength={20000}
          placeholder="Add notes about this run — config, observations, anomalies, follow-ups…"
          className="w-full resize-y rounded-md border border-border/60 bg-background p-3 text-sm shadow-sm outline-none focus:border-lime-600/40 focus:ring-1 focus:ring-lime-600/20 disabled:opacity-50"
        />
      </CardContent>
    </Card>
  );
};
